import {memo, useEffect} from 'react'

import {useSpotCollection, useSpotGet} from '../hooks/entity/useSpot'
import {getSpotUrl} from '../hooks/useSpotNavigate'
import {notFoundIdentifier, useStateCurrentSpotId} from '../states/currentSpot'
import {setPromptEnable} from '../states/promptEnable'
import {ApiError} from '../utils/ApiError'

import {useStableNavigate, useStableParamValue} from './StableRouter'

export const CurrentSpotIdListener = memo(function CurrentSpotIdListener() {
  const [currentSpotId, setCurrentSpotId] = useStateCurrentSpotId()
  const spotSlug = useStableParamValue('spotSlug')
  const {spots} = useSpotCollection(spotSlug ? {slug: spotSlug} : undefined)
  const {spot, error} = useSpotGet(currentSpotId?.iri)

  const navigate = useStableNavigate()

  useEffect(() => {
    if (!spots) {
      if (!spotSlug) {
        setCurrentSpotId(undefined)
      }
      return
    }

    const spot = spots.member[0]
    const newIdentifier = spot ? {
      iri: spot['@id'],
      slug: spot.slug,
    } : notFoundIdentifier

    setCurrentSpotId((oldIdentifier) => {
      // No slug => undefined in all ways
      if (!spotSlug) {
        return undefined
      }

      // The new identifier is better than the old
      if (!oldIdentifier || oldIdentifier === notFoundIdentifier) {
        return newIdentifier
      }

      // The new identifier is not yet fully loaded
      if (newIdentifier === notFoundIdentifier) {
        // The old identifier still match
        if (oldIdentifier.slug === spotSlug) {
          return oldIdentifier
        }

        return newIdentifier
      }

      // return old identifier to get the same object reference
      if (JSON.stringify(oldIdentifier) === JSON.stringify(newIdentifier)) {
        return oldIdentifier
      }

      return newIdentifier
    })
  }, [setCurrentSpotId, spotSlug, spots])

  useEffect(() => {
    if (!currentSpotId || currentSpotId === notFoundIdentifier) {
      return
    }
    if (error && error instanceof ApiError && error.statusCode === 404) {
      setCurrentSpotId(notFoundIdentifier)

      return
    }

    if (!spot) {
      // an error occurred
      return
    }

    if (spot.slug === currentSpotId.slug) {
      return
    }
    const oldSpotPath = getSpotUrl(currentSpotId.slug, '/')
    const location = `${window.location.pathname}${window.location.search}`
    if (!location.startsWith(oldSpotPath)) {
      return
    }

    setPromptEnable(false)
    navigate(getSpotUrl(spot.slug, location.substring(oldSpotPath.length - 1)))
    setPromptEnable(true)

    setCurrentSpotId({
      iri: spot['@id'],
      slug: spot.slug,
    })
  }, [error, navigate, setCurrentSpotId, spot, currentSpotId])

  return null
})
